'use client'

import { useRef, useState } from 'react'
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import clsx from 'clsx'
import Slide from './Slide'
import Thumbnail from './Thumbnail'
import ArrowIcon from '@/app/_components/icons/ArrowIcon'
import { Image } from '@/api'
import { imageUrl } from '@/lib/utils'
import { Plus } from 'lucide-react'

interface ListingImageGalleryProps {
  productImages?: Image[]
  listingImages: Image[]
  isSoldOut?: boolean
  className?: string
  onImageClick?: (images: string[], currentIndex: number) => void
}

const createThumbId = (index: number) => `product-detail-${index}`

export const ListingImageGallery = ({
  listingImages = [],
  isSoldOut = false,
  className = '',
  onImageClick,
}: ListingImageGalleryProps) => {
  const swiperRef = useRef<SwiperRef['swiper'] | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)

  const allItems = listingImages

  const handleImageClick = () => {
    if (onImageClick && allItems.length > 0) {
      const allImageUrls = allItems.map(imageUrl)
      onImageClick(allImageUrls, activeIndex)
    }
  }

  return (
    <div className={`w-full lg:w-[42%] ${className}`}>
      <div className="relative">
        {isSoldOut && (
          <div className="absolute top-0 left-0 z-10">
            <div className="[clip-path:polygon(0_0,100%_0,0_100%)] bg-black w-[77px] h-[77px] lg:w-[154px] lg:h-[154px]"></div>
            <span className="absolute top-[17px] left-[8px] lg:top-[33px] lg:left-[15px] text-white transform -rotate-45 text-xs lg:text-2xl font-bold">
              SOLD
            </span>
          </div>
        )}
        <Swiper
          slidesPerView={1}
          navigation={{
            prevEl: '.button_prev',
            nextEl: '.button_next',
          }}
          modules={[Navigation]}
          onActiveIndexChange={(swiper) => {
            setActiveIndex(swiper.activeIndex)

            const productDetailId = createThumbId(swiper.activeIndex)
            const element = document.getElementById(productDetailId)
            element?.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
            })
          }}
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          className="relative cursor-zoom-in"
          onClick={handleImageClick}
        >
          {allItems.map((item, index) => (
            <SwiperSlide key={index} className="relative">
              <Slide src={item} />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                <div className="p-4 border-2 border-white bg-black/20">
                  <Plus className="w-12 h-12 text-white" />
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <span
          className={clsx(
            'button_prev w-7 h-7 lg:w-8 lg:h-8 cursor-pointer absolute top-1/2 -translate-y-2/4 left-3 z-10 -rotate-90 flex items-center justify-center rounded-full bg-white',
            activeIndex === 0 && 'opacity-40'
          )}
        >
          <ArrowIcon className="lg:h-4 lg:w-4" color="#000" />
        </span>
        <span
          className={clsx(
            'button_next w-7 h-7 lg:w-8 lg:h-8 cursor-pointer absolute top-1/2 -translate-y-2/4 right-3 z-10 rotate-90 flex items-center justify-center rounded-full bg-white',
            activeIndex === allItems.length - 1 && 'opacity-40'
          )}
        >
          <ArrowIcon className="lg:h-4 lg:w-4" color="#000" />
        </span>
      </div>
      <div>
        <div className="flex mt-3 gap-0.5 overflow-scroll hidden-scrollbar">
          {listingImages.map((item, index) => (
            <Thumbnail
              id={createThumbId(index)}
              key={`listing-thumb-${index}`}
              src={item}
              onClick={() => swiperRef?.current?.slideTo(index)}
              active={activeIndex === index}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
